import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Layers, Shield, BarChart3, ArrowRight, CheckCircle, TrendingUp, Users } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

function FeatureCard({ icon: Icon, title, children }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-8 card-hover">
      <div className="w-12 h-12 rounded-xl bg-navy/5 flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-navy" aria-hidden="true" />
      </div>
      <h3 className="font-heading font-semibold text-lg text-navy mb-3">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{children}</p>
    </article>
  );
}

export default function Suppliers() {
  // Apply SEO meta tags
  useSEO('suppliers');

  return (
    <Layout>
      {/* Hero */}
      <section 
        className="relative bg-slate-50 overflow-hidden"
        data-testid="suppliers-hero"
        aria-labelledby="suppliers-heading"
      >
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <header>
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                For Beverage Suppliers
              </span>
              <h1 
                id="suppliers-heading"
                className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight mb-6"
              >
                Expand Your Reach Through Coalition
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8 max-w-xl">
                BevRelay helps mid-tier beverage suppliers expand account access through a curated coalition portfolio, 
                protected relationship sharing, and a clean intro workflow. This is not a marketplace — it's a relationship network.
              </p>
              <Link
                to="/request-access"
                data-testid="suppliers-cta-btn"
                className="btn-copper"
                aria-label="Apply for supplier access to BevRelay"
              >
                Apply for Supplier Access
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </header>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHByb2Zlc3Npb25hbCUyMG1lZXRpbmd8ZW58MHx8fHwxNzczMTQxMTg2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Two professionals shaking hands representing BevRelay supplier partnerships"
                  className="w-full h-80 object-cover"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-copper/10 rounded-full blur-2xl" aria-hidden="true" />
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-navy/10 rounded-full blur-3xl" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Benefits - Structured for featured snippets */}
      <section 
        className="section-padding bg-white" 
        data-testid="suppliers-features"
        aria-labelledby="supplier-benefits-heading"
      >
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="supplier-benefits-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Why Suppliers Choose BevRelay
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Built by suppliers, for suppliers. Every feature designed to protect and expand your business relationships.
            </p>
          </header>

          <div className="grid gap-8 md:grid-cols-3">
            <FeatureCard icon={Layers} title="Coalition Portfolio">
              Category seats are limited to keep the roster high signal and non-competing. 
              Quality over quantity, always. Your brand stands out among carefully curated peers.
            </FeatureCard>
            <FeatureCard icon={Shield} title="Permissioned Introductions">
              Relationship owners approve all intros. Your contacts stay protected until you approve. 
              Full control over who gets access to your network.
            </FeatureCard>
            <FeatureCard icon={BarChart3} title="Outcome Tracking">
              Track every request from intro → meeting → placement → reorder. 
              Full visibility on every opportunity with measurable results.
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* How It Works for Suppliers */}
      <section 
        className="section-padding bg-slate-50"
        aria-labelledby="how-suppliers-work-heading"
        itemScope 
        itemType="https://schema.org/HowTo"
      >
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="how-suppliers-work-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4" itemProp="name">
              How BevRelay Works for Suppliers
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" itemProp="description">
              A streamlined process to expand your reach while protecting your relationships
            </p>
          </header>

          <div className="grid gap-6 md:grid-cols-4">
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="step">
              <meta itemProp="position" content="1" />
              <div className="w-12 h-12 rounded-full bg-copper text-white flex items-center justify-center font-bold text-lg mx-auto mb-4">1</div>
              <h3 className="font-semibold text-navy mb-2" itemProp="name">Create Profile</h3>
              <p className="text-sm text-slate-600" itemProp="text">Upload your deck, pricing, and market information</p>
            </div>
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="step">
              <meta itemProp="position" content="2" />
              <div className="w-12 h-12 rounded-full bg-copper text-white flex items-center justify-center font-bold text-lg mx-auto mb-4">2</div>
              <h3 className="font-semibold text-navy mb-2" itemProp="name">Get Curated</h3>
              <p className="text-sm text-slate-600" itemProp="text">Category seats are reviewed and approved by BevRelay</p>
            </div>
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="step">
              <meta itemProp="position" content="3" />
              <div className="w-12 h-12 rounded-full bg-copper text-white flex items-center justify-center font-bold text-lg mx-auto mb-4">3</div>
              <h3 className="font-semibold text-navy mb-2" itemProp="name">Receive Requests</h3>
              <p className="text-sm text-slate-600" itemProp="text">Buyers discover you and request introductions</p>
            </div>
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="step">
              <meta itemProp="position" content="4" />
              <div className="w-12 h-12 rounded-full bg-copper text-white flex items-center justify-center font-bold text-lg mx-auto mb-4">4</div>
              <h3 className="font-semibold text-navy mb-2" itemProp="name">Track Outcomes</h3>
              <p className="text-sm text-slate-600" itemProp="text">Monitor results from intro to placement to reorder</p>
            </div>
          </div>
        </div>
      </section>

      {/* Supplier Benefits List */}
      <section className="section-padding bg-white" aria-labelledby="benefits-list-heading">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 id="benefits-list-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy leading-tight mb-6">
                What You Get as a Supplier
              </h2>
              <ul className="space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-slate-600">Protected relationship sharing with full approval control</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-slate-600">Access to verified buyer network across multiple markets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-slate-600">Non-competing category placement with limited seats</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-slate-600">End-to-end outcome tracking and analytics</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-slate-600">Coalition support and strategic introductions</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-navy p-6 text-white">
                <TrendingUp className="w-8 h-8 text-copper mb-3" aria-hidden="true" />
                <div className="text-2xl font-bold mb-1">3x</div>
                <div className="text-sm text-white/70">Average reach expansion</div>
              </div>
              <div className="rounded-2xl bg-slate-100 p-6">
                <Users className="w-8 h-8 text-navy mb-3" aria-hidden="true" />
                <div className="text-2xl font-bold text-navy mb-1">100%</div>
                <div className="text-sm text-slate-600">Relationship protection</div>
              </div>
              <div className="rounded-2xl bg-slate-100 p-6">
                <Shield className="w-8 h-8 text-navy mb-3" aria-hidden="true" />
                <div className="text-2xl font-bold text-navy mb-1">Limited</div>
                <div className="text-sm text-slate-600">Category seats</div>
              </div>
              <div className="rounded-2xl bg-copper p-6 text-white">
                <BarChart3 className="w-8 h-8 text-white mb-3" aria-hidden="true" />
                <div className="text-2xl font-bold mb-1">E2E</div>
                <div className="text-sm text-white/80">Outcome tracking</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-50" data-testid="suppliers-cta" aria-labelledby="supplier-cta-heading">
        <div className="container-custom">
          {/* Coalition Assets - Trade Shows */}
          <div className="rounded-3xl bg-white border border-slate-200 p-8 md:p-12 mb-12">
            <div className="text-center mb-8">
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-3">
                Coalition Assets
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy">
                Go Bigger Together
              </h2>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-copper/5 to-copper/10 rounded-2xl p-6 md:p-8 border border-copper/20">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-copper/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-copper" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-navy mb-2">
                      Trade Shows
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      Combined budgets mean we go bigger — more shows, better booths, stronger presence. 
                      Live in New York but want to show in California? Coalition partnerships are handled 
                      by Regional Leads who represent the portfolio on your behalf.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Save $3,000+ on travel, shipping, and setup
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-navy p-10 md:p-16">
            <div className="max-w-2xl">
              <h2 id="supplier-cta-heading" className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
                Apply for Supplier Access
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Upload your deck, pricing, and markets. Founding suppliers are curated by category to ensure 
                a high-quality, non-competing coalition portfolio.
              </p>
              <Link
                to="/request-access"
                data-testid="suppliers-bottom-cta-btn"
                className="btn-copper"
              >
                Request Access
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
